import { RefreshCw } from "lucide-react";
import { Navigate, useNavigate } from "react-router";

import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { Loader } from "@/components/common/Loader";
import { StockCard } from "@/components/common/StockCard";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import type { ProductStockDto, ProvisionStockDto } from "@/types/stock.types";

/**
 * Both stock sections list the same shape. With a single stock the list is a
 * click for nothing, so it hands straight over to the detail screen.
 */
export function StockListPage({
  title,
  subtitle,
  basePath,
  stocks,
  isLoading,
  error,
  onRetry,
  emptyHint,
  kind,
}: {
  title: string;
  subtitle: string;
  basePath: string;
  stocks: Array<ProvisionStockDto | ProductStockDto>;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  emptyHint: string;
  kind: string;
}) {
  const navigate = useNavigate();
  const { currency } = useCurrency();

  if (isLoading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  if (stocks.length === 1 && stocks[0]) {
    return <Navigate to={`${basePath}/${stocks[0].id}`} replace />;
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={title} subtitle={subtitle} />

      {stocks.length === 0 ? (
        <EmptyState
          title="Aucun stock"
          hint={emptyHint}
          action={
            <div className="flex flex-col items-center gap-3">
              <Button type="button" variant="outline" onClick={onRetry}>
                <RefreshCw />
                Actualiser
              </Button>
            </div>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {stocks.map((stock) => (
            <StockCard
              key={stock.id}
              id={stock.id}
              name={stock.name}
              description={stock.description}
              createdAt={stock.createdAt}
              kind={kind}
              currency={currency}
              onClick={(id) => navigate(`${basePath}/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
